import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Email from '@hellodarwin/icons/dist/icons/Email';
import Employees from '@hellodarwin/icons/dist/icons/Employees';
import HubspotLogo from '@hellodarwin/icons/dist/icons/HubspotLogo';
import IdIcon from '@hellodarwin/icons/dist/icons/IdIcon';
import Invoices from '@hellodarwin/icons/dist/icons/Invoices';
import Link from '@hellodarwin/icons/dist/icons/Link';
import Location from '@hellodarwin/icons/dist/icons/Location';
import NewWindow from '@hellodarwin/icons/dist/icons/NewWindow';
import Phone from '@hellodarwin/icons/dist/icons/Phone';
import Timeline from '@hellodarwin/icons/dist/icons/Timeline';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { dollarFormat } from '../../../features/helpers';
import { AnnualRevenueTranslationKeys } from '../../../features/helpers/company-infos';
import formatPhoneNumber from '../../../features/helpers/form/format-phone-number';
import { getStringDateFromString } from '../../../features/helpers/get-formatted-date';
import parse from '../../../features/helpers/parse';
import prefixHTTPS from '../../../features/helpers/prefix-https';
import useLocale from '../../../features/providers/locale-provider';
import { usePlatform } from '../../../features/providers/platform-provider';
import { useTranslations } from '../../../features/providers/translations-provider';
import Div from '../../common/div';
import HdTag from '../../common/hd-tag';
import BusinessProfileInfoRow from './business-profile-info-row';
import { BusinessProfileList } from './styles';
const buildOptionLabelMap = (options) => {
    const mappedOptions = {};
    options === null || options === void 0 ? void 0 : options.forEach((option) => {
        if ((option === null || option === void 0 ? void 0 : option.value) == null) {
            return;
        }
        mappedOptions[String(option.value)] = option.label;
    });
    return mappedOptions;
};
const BusinessProfile = ({ company, industries, subindustries, naicsCodes, industriesICNPO, npoBeneficiaries, npoScopes, npoStreams, columnCount, hideDescription = false, hideHubspotLink = false, }) => {
    const { t } = useTranslations();
    const theme = useTheme();
    const { selectedLocale } = useLocale();
    const { app } = usePlatform();
    const isAdmin = app === 'admin';
    const { program_description } = company;
    const mappedIndustries = useMemo(() => buildOptionLabelMap(industries), [industries]);
    const mappedSubindustries = useMemo(() => buildOptionLabelMap(subindustries), [subindustries]);
    const mappedNaicsCodes = useMemo(() => buildOptionLabelMap(naicsCodes), [naicsCodes]);
    const mappedIndustriesICNPO = useMemo(() => buildOptionLabelMap(industriesICNPO), [industriesICNPO]);
    const mappedNpoBeneficiaries = useMemo(() => buildOptionLabelMap(npoBeneficiaries), [npoBeneficiaries]);
    const mappedNpoScopes = useMemo(() => buildOptionLabelMap(npoScopes), [npoScopes]);
    const mappedNpoStreams = useMemo(() => buildOptionLabelMap(npoStreams), [npoStreams]);
    const isRichTextEmpty = (htmlString) => {
        var _a;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;
        tempDiv.querySelectorAll('br').forEach((br) => br.remove());
        return ((_a = tempDiv.textContent) === null || _a === void 0 ? void 0 : _a.trim()) === '';
    };
    const infos = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const { created_at, phone, website, email, size, annual_revenue, exact_annual_revenue, exact_company_size, business_number, cra_number, address, city, province, postal_code, country, hubspot_url, industry_sector, industry_subsector, naics_code, fiscal_year_end, } = company;
        const annualRevenueTranslationKey = annual_revenue
            ? AnnualRevenueTranslationKeys[annual_revenue]
            : undefined;
        const formattedPhone = !!phone ? formatPhoneNumber(phone) : undefined;
        const formattedWebsite = !!website
            ? prefixHTTPS(website).toLowerCase()
            : undefined;
        return [
            isAdmin && {
                label: t('company|clientSince'),
                Icon: Timeline,
                value: !!created_at
                    ? getStringDateFromString(created_at, selectedLocale)
                    : '',
            },
            isAdmin &&
                !hideHubspotLink && {
                label: 'Hubspot',
                Icon: HubspotLogo,
                value: !!hubspot_url ? (_jsxs("a", { href: hubspot_url, target: "_blank", children: [t('company|hubspotLink'), " ", _jsx(NewWindow, { size: 16 })] })) : (''),
            },
            isAdmin && {
                label: t('company|phone'),
                Icon: Phone,
                value: !!formattedPhone ? (_jsx("a", { href: `tel:${formattedPhone}`, children: formattedPhone })) : (''),
            },
            isAdmin && {
                label: t('company|companyEmail'),
                Icon: Email,
                value: !!email ? _jsx("a", { href: `mailto:${email}`, children: email }) : ' - ',
            },
            isAdmin && {
                label: t('company|website'),
                Icon: Link,
                value: !!formattedWebsite ? (_jsx("a", { href: `${formattedWebsite}`, target: "_blank", rel: "noreferrer", children: formattedWebsite })) : (' - '),
            },
            (isAdmin || business_number) && {
                label: t('company|businessNumber'),
                Icon: IdIcon,
                value: business_number !== null && business_number !== void 0 ? business_number : ' - ',
            },
            isAdmin && {
                label: t('company|craNumber'),
                Icon: IdIcon,
                value: cra_number !== null && cra_number !== void 0 ? cra_number : ' - ',
            },
            (isAdmin || annual_revenue) && {
                label: t('company|annualRevenue'),
                Icon: Invoices,
                value: annualRevenueTranslationKey
                    ? t(annualRevenueTranslationKey)
                    : ' - ',
            },
            (isAdmin || exact_annual_revenue) && {
                label: t('company|exactAnnualRevenue'),
                Icon: Invoices,
                value: exact_annual_revenue
                    ? dollarFormat(exact_annual_revenue, selectedLocale, true)
                    : ' - ',
            },
            (isAdmin || !!(size === null || size === void 0 ? void 0 : size.length)) && {
                label: t('company|companySize'),
                Icon: Employees,
                value: !!(size === null || size === void 0 ? void 0 : size.length) ? t(`company|companySizeValue`, { size }) : ' - ',
            },
            (isAdmin || exact_company_size) && {
                label: t('company|exactCompanySize'),
                Icon: Employees,
                value: exact_company_size ? `${exact_company_size}` : ' - ',
            },
            {
                label: t('company|companyAddress'),
                Icon: Location,
                value: (_b = (_a = [address, city, province, country, postal_code]) === null || _a === void 0 ? void 0 : _a.filter((i) => !!i)) === null || _b === void 0 ? void 0 : _b.join(', '),
            },
            isAdmin && {
                label: t('company|industry'),
                Icon: Employees,
                value: (industry_sector === null || industry_sector === void 0 ? void 0 : industry_sector.length) || (industry_subsector === null || industry_subsector === void 0 ? void 0 : industry_subsector.length) ? (_jsxs(Div, { flex: "row", wrap: "wrap", gap: 8, children: [industry_sector === null || industry_sector === void 0 ? void 0 : industry_sector.map((tag) => (_jsx(HdTag, { text: mappedIndustries[tag], color: theme.colors.purple_2 }, tag))), industry_subsector === null || industry_subsector === void 0 ? void 0 : industry_subsector.map((tag) => (_jsx(HdTag, { text: mappedSubindustries[tag], color: theme.colors.purple_3 }, tag)))] })) : ('-'),
            },
            naics_code &&
                Object.keys(mappedNaicsCodes).length && {
                label: t('company|naicsCode'),
                Icon: IdIcon,
                value: (naics_code === null || naics_code === void 0 ? void 0 : naics_code.length) ? (_jsx(Div, { flex: "row", wrap: "wrap", gap: 8, children: naics_code === null || naics_code === void 0 ? void 0 : naics_code.map((tag) => (_jsx(HdTag, { text: mappedNaicsCodes[tag], color: theme.colors.purple_4 }, tag))) })) : ('-'),
            },
            isAdmin &&
                ((_c = company.industries_icnpo) === null || _c === void 0 ? void 0 : _c.length) &&
                Object.keys(mappedIndustriesICNPO).length && {
                label: t('companyForm|industriesICNPO'),
                Icon: Location,
                value: (_jsx(Div, { flex: "row", wrap: "wrap", gap: 8, children: (_d = company.industries_icnpo) === null || _d === void 0 ? void 0 : _d.map((tag) => (_jsx(HdTag, { text: mappedIndustriesICNPO[tag], color: theme.colors.purple_2 }, tag))) })),
            },
            isAdmin &&
                ((_e = company.npo_beneficiaries) === null || _e === void 0 ? void 0 : _e.length) &&
                Object.keys(mappedNpoBeneficiaries).length && {
                label: t('companyForm|npoBeneficiaries'),
                Icon: Employees,
                value: (_jsx(Div, { flex: "row", wrap: "wrap", gap: 8, children: (_f = company.npo_beneficiaries) === null || _f === void 0 ? void 0 : _f.map((tag) => (_jsx(HdTag, { text: mappedNpoBeneficiaries[tag], color: theme.colors.purple_3 }, tag))) })),
            },
            isAdmin &&
                ((_g = company.npo_scopes) === null || _g === void 0 ? void 0 : _g.length) &&
                Object.keys(mappedNpoScopes).length && {
                label: t('companyForm|npoScopes'),
                Icon: Location,
                value: (_jsx(Div, { flex: "row", wrap: "wrap", gap: 8, children: (_h = company.npo_scopes) === null || _h === void 0 ? void 0 : _h.map((tag) => (_jsx(HdTag, { text: mappedNpoScopes[tag], color: theme.colors.purple_4 }, tag))) })),
            },
            isAdmin &&
                company.npo_stream_id &&
                Object.keys(mappedNpoStreams).length && {
                label: t('companyForm|npoStream'),
                Icon: Invoices,
                value: mappedNpoStreams[company.npo_stream_id] || company.npo_stream_id,
            },
            isAdmin && {
                label: t('company|fiscalYearEnd'),
                Icon: Timeline,
                value: !!fiscal_year_end
                    ? getStringDateFromString(fiscal_year_end, selectedLocale)
                    : '-',
            },
        ].filter((itm) => !!itm);
    }, [
        company,
        mappedIndustries,
        mappedSubindustries,
        mappedNaicsCodes,
        mappedIndustriesICNPO,
        mappedNpoBeneficiaries,
        mappedNpoScopes,
        mappedNpoStreams,
        hideHubspotLink,
        isAdmin,
        selectedLocale,
        t,
        theme.colors.purple_2,
        theme.colors.purple_3,
        theme.colors.purple_4,
    ]);
    return (_jsxs(Div, { flex: "column", gap: 32, children: [!hideDescription &&
                !!program_description &&
                !isRichTextEmpty(program_description) && (_jsx("div", { children: parse(program_description) })), _jsx(BusinessProfileList, { "$columnCount": columnCount, children: infos.map((info, index) => (_jsx(BusinessProfileInfoRow, Object.assign({}, info), `business-profile-info-row-${index}`))) })] }));
};
export default BusinessProfile;
